import React, { useEffect, useState } from "react";
import { Typography, Box, List, ListItem, ListItemButton, ListItemText, CircularProgress } from "@mui/material";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./SettingsPanel.scss";
import GGModalWindow from "../../../UtilityComponents/GGModalWindow/GGModalWindow";
import ChangePasswordWindow from "../../AuthPageComponents/ChangePasswordPage/ChangePasswordWindow";
import { SellerInterface } from "../../../../Models/Seller";
import { SellerAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import { PaymentSettingsSubPanel } from "./PaymentSettingsSubPanel";
import { ErrorLogger } from "../../../../utils/Logger";


export enum SellerSettingsType {
  PAYMENT_SETTINGS,
  PROFILE_SETTINGS,
  PASSWORD_SETTINGS,
}

export interface SellerSettingsConfigEntry {
  type: SellerSettingsType;
  label: string;
  description: string;
}

const SettingsList = () => {
    const navigate = useNavigate();
    const [seller, setSeller] = useState<SellerInterface | null>(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const listOfSettings: SellerSettingsConfigEntry[] = [
        {
            type: SellerSettingsType.PAYMENT_SETTINGS,
            label: "Payment Settings",
            description: "Update your saved payments details and default payment methods for receiving money.",
        },
        {
            type: SellerSettingsType.PROFILE_SETTINGS,
            label: "Profile Settings",
            description: "Update your personal information, including your name, occupation and profile picture.",
        },
        {
            type: SellerSettingsType.PASSWORD_SETTINGS,
            label: "Password Settings",
            description: "Change your password to keep your account secure.",
        },
    ];

    useEffect(() => {
        const fetchSeller = async () => {
            setIsLoading(true);
            try {
                const sellerData = await SellerAPIHandler.shared.getSellerByAuthId();
                setSeller(sellerData);
            } catch (err) {
                ErrorLogger.log(err, 'SettingsPanel.SettingsList.fetchSeller');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSeller();
    }, []);

    const handleSettingClick = (setting: SellerSettingsConfigEntry) => {
        switch (setting.type) {
            case SellerSettingsType.PROFILE_SETTINGS:
                navigate(`/my-profile`);
                break;
            case SellerSettingsType.PASSWORD_SETTINGS:
                setShowChangePasswordModal(true);
                break;
            case SellerSettingsType.PAYMENT_SETTINGS:
                navigate("/freelancer-dashboard/settings/payment");
                break;
        }
    };

    if (isLoading) {
        return (
            <section className="settings-panel">
                <Box 
                    className="settings-panel-content"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px'
                    }}
                >
                    <CircularProgress />
                </Box>
            </section>
        );
    }

    if (!seller) {
        return (
            <section className="settings-panel">
                <Box className="settings-panel-content">
                    <Typography variant="h6" sx={{ mb: 2, color: "error.main" }}>
                        Unable to load seller information. Please try again later.
                    </Typography>
                </Box>
            </section>
        );
    }

    return (
        <>
            <GGModalWindow
                childView={
                    <ChangePasswordWindow
                        email={seller.email ?? ""}
                        passwordChangeCompletion={() => setShowChangePasswordModal(false)}
                    />
                }
                open={showChangePasswordModal}
                onCloseHandler={() => setShowChangePasswordModal(false)}
                title="Change Password"
            />
            <Box className="settings-panel-content">
                <Typography variant="body1" className="settings-panel-description" sx={{ color: "#666", mb: 3 }}>
                    Manage your account settings, update your profile, change your password, and configure payment methods.
                </Typography>
                <div className="settings-list-container">
                    <List>
                        {listOfSettings.map((item) => (
                            <ListItem key={item.label} disablePadding sx={{ mb: 1.5 }}>
                                <ListItemButton
                                    className="settings-list-item-btn"
                                    onClick={() => handleSettingClick(item)}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography fontWeight={600} fontSize="1rem">
                                                {item.label}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                                                {item.description}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </div>
            </Box>
        </>
    );
};

export const SettingsPanel = () => {
    return (
        <section className="settings-panel">
            <Routes>
                <Route index element={<SettingsList />} />
                <Route path="payment" element={<PaymentSettingsSubPanel />} />
            </Routes>
        </section>
    );
};

